/**
 * 智能底部悬浮指令
 * 用法: v-sticky-bottom="{ sentinel: '.comment-wrap', container: '.content', offset: 0 }"
 * - sentinel: 必填，作为吸附判断基准的元素选择器（通常是评论列表容器）
 * - container: 可选，悬浮状态时需要对齐宽度和位置的容器选择器（默认 '.content'）
 * - offset: 可选，吸附触发的额外偏移量
 *
 * 行为：
 * - 当 sentinel 元素底部完全进入视口时，评论框切换为 relative（is-static），吸附到评论列表下方
 * - 其他时候评论框 fixed（is-fixed）悬浮在视口底部，宽度和位置自动对齐到 container 容器
 */

const StickyBottom = {
  bind(el, binding) {
    const options = binding.value || {}
    const sentinelSelector = options.sentinel
    const containerSelector = options.container || '.content'
    const offset = options.offset || 0

    if (!sentinelSelector) {
      console.warn('[v-sticky-bottom] sentinel selector is required')
      return
    }

    el._stickyBottomConfig = { sentinelSelector, containerSelector, offset }

    el._stickyBottomInitTimer = setTimeout(() => {
      initListeners(el)
      checkState(el)
    }, 300)
  },

  update(el) {
    el._stickyBottomUpdateTimer = setTimeout(() => {
      checkState(el)
    }, 300)
  },

  unbind(el) {
    if (el._stickyBottomInitTimer) {
      clearTimeout(el._stickyBottomInitTimer)
    }
    if (el._stickyBottomUpdateTimer) {
      clearTimeout(el._stickyBottomUpdateTimer)
    }
    if (el._stickyBottomScrollHandler) {
      window.removeEventListener('scroll', el._stickyBottomScrollHandler, { passive: true })
    }
    if (el._stickyBottomResizeHandler) {
      window.removeEventListener('resize', el._stickyBottomResizeHandler, { passive: true })
    }
    if (el._stickyBottomResizeObserver) {
      el._stickyBottomResizeObserver.disconnect()
    }
    el._stickyBottomScrollHandler = null
    el._stickyBottomResizeHandler = null
    el._stickyBottomResizeObserver = null
    el._stickyBottomConfig = null
  }
}

function checkState(el) {
  const config = el._stickyBottomConfig
  if (!config) return

  const sentinelContainer = document.querySelector(config.sentinelSelector)

  // 没有评论列表（sentinel 不存在）时，评论框始终悬浮在底部，对齐内容区宽度
  if (!sentinelContainer) {
    el.classList.add('is-fixed')
    el.classList.remove('is-static')
    alignToContainer(el, config.containerSelector)
    return
  }

  const containerRect = sentinelContainer.getBoundingClientRect()
  const viewportHeight = window.innerHeight

  if (containerRect.bottom <= viewportHeight) {
    el.classList.add('is-static')
    el.classList.remove('is-fixed')
    el.style.width = ''
    el.style.left = ''
  } else {
    el.classList.add('is-fixed')
    el.classList.remove('is-static')
    alignToContainer(el, config.containerSelector)
  }
}

function alignToContainer(el, containerSelector) {
  const container = document.querySelector(containerSelector)
  if (!container) return

  const rect = container.getBoundingClientRect()
  el.style.width = rect.width + 'px'
  el.style.left = rect.left + 'px'
}

function initListeners(el) {
  const config = el._stickyBottomConfig

  el._stickyBottomScrollHandler = throttle(() => {
    checkState(el)
  }, 16)

  el._stickyBottomResizeHandler = throttle(() => {
    checkState(el)
  }, 100)

  window.addEventListener('scroll', el._stickyBottomScrollHandler, { passive: true })
  window.addEventListener('resize', el._stickyBottomResizeHandler, { passive: true })

  // 使用 ResizeObserver 监听容器尺寸变化，实时重新对齐
  if (typeof ResizeObserver !== 'undefined' && config.containerSelector) {
    const container = document.querySelector(config.containerSelector)
    if (container) {
      el._stickyBottomResizeObserver = new ResizeObserver(throttle(() => {
        checkState(el)
      }, 100))
      el._stickyBottomResizeObserver.observe(container)
    }
  }
}

function throttle(func, limit) {
  let inThrottle = false
  return function() {
    const args = arguments
    const context = this
    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export default StickyBottom
